import { useMutation, useQuery } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENTITY_TYPES, ENTITY_TYPE_CREATE, ENTITY_TYPE_UPDATE } from 'services/reservations';

interface EntityType {
    id: string;
    name: string;
    slug: string;
    description: string;
    icon: string;
    color: string;
    minDurationMinutes: number;
    maxDurationMinutes: number;
    slotIntervalMinutes: number;
    advanceBookingDays: number;
    sameDayBooking: boolean;
    maxActiveBookingsPerMember: number;
    requiresApproval: boolean;
    cancellationDeadlineHours: number;
    isActive: boolean;
}

const defaultFormData = {
    name: '',
    slug: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    minDurationMinutes: 30,
    maxDurationMinutes: 480,
    slotIntervalMinutes: 30,
    advanceBookingDays: 30,
    sameDayBooking: true,
    maxActiveBookingsPerMember: 3,
    requiresApproval: false,
    cancellationDeadlineHours: 24,
};

export const EntityTypesList: React.FC = () => {
    const { t } = useTranslation('translation');
    const [showNew, setShowNew] = useState(false);
    const [isEditing, setIsEditing] = useState<string | null>(null);
    const [formData, setFormData] = useState(defaultFormData);

    const { data, loading, refetch } = useQuery(ENTITY_TYPES);

    const [createEntityType, { loading: creating }] = useMutation(ENTITY_TYPE_CREATE, {
        onCompleted: () => {
            setFormData(defaultFormData);
            setShowNew(false);
            refetch();
        },
    });

    const [updateEntityType, { loading: updating }] = useMutation(ENTITY_TYPE_UPDATE, {
        onCompleted: () => {
            setIsEditing(null);
            refetch();
        },
    });

    const generateSlug = (name: string) => {
        return name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/(^-|-$)/g, '');
    };

    const handleCreate = () => {
        if (formData.name.trim() && formData.slug.trim()) {
            createEntityType({
                variables: {
                    input: {
                        ...formData,
                        isActive: true,
                    },
                },
            });
        }
    };

    const handleUpdate = (id: string) => {
        updateEntityType({
            variables: {
                id,
                input: formData,
            },
        });
    };

    const handleEdit = (entityType: EntityType) => {
        setIsEditing(entityType.id);
        setFormData({
            name: entityType.name,
            slug: entityType.slug,
            description: entityType.description,
            icon: entityType.icon,
            color: entityType.color,
            minDurationMinutes: entityType.minDurationMinutes,
            maxDurationMinutes: entityType.maxDurationMinutes,
            slotIntervalMinutes: entityType.slotIntervalMinutes,
            advanceBookingDays: entityType.advanceBookingDays,
            sameDayBooking: entityType.sameDayBooking,
            maxActiveBookingsPerMember: entityType.maxActiveBookingsPerMember,
            requiresApproval: entityType.requiresApproval,
            cancellationDeadlineHours: entityType.cancellationDeadlineHours,
        });
    };

    if (loading) return <Loader />;

    const entityTypes: EntityType[] = data?.entityTypes?.edges?.map((edge: any) => edge.node) || [];

    return (
        <Container>
            <div className="flex justify-between items-center mb-6">
                <PageTitle>{t('reservations.entityTypes')}</PageTitle>
                <Button onClick={() => setShowNew(true)}>{t('reservations.addEntityType')}</Button>
            </div>

            {showNew && (
                <div className="mb-6 p-4 bg-white rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">{t('reservations.newEntityType')}</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.name')}</label>
                            <Input
                                value={formData.name}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                        slug: generateSlug(e.target.value),
                                    });
                                }}
                                placeholder={t('reservations.placeholderEntityTypeName')}
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.slug')}</label>
                            <Input
                                value={formData.slug}
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                placeholder={t('reservations.placeholderSlug')}
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.minDuration')}</label>
                            <Input
                                type="number"
                                value={formData.minDurationMinutes}
                                onChange={(e) =>
                                    setFormData({ ...formData, minDurationMinutes: parseInt(e.target.value) })
                                }
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.maxDuration')}</label>
                            <Input
                                type="number"
                                value={formData.maxDurationMinutes}
                                onChange={(e) =>
                                    setFormData({ ...formData, maxDurationMinutes: parseInt(e.target.value) })
                                }
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.slotInterval')}</label>
                            <Input
                                type="number"
                                value={formData.slotIntervalMinutes}
                                onChange={(e) =>
                                    setFormData({ ...formData, slotIntervalMinutes: parseInt(e.target.value) })
                                }
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.advanceBooking')}</label>
                            <Input
                                type="number"
                                value={formData.advanceBookingDays}
                                onChange={(e) =>
                                    setFormData({ ...formData, advanceBookingDays: parseInt(e.target.value) })
                                }
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.color')}</label>
                            <Input
                                type="color"
                                value={formData.color}
                                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                disabled={creating}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.cancellationDeadline')}</label>
                            <Input
                                type="number"
                                value={formData.cancellationDeadlineHours}
                                onChange={(e) =>
                                    setFormData({ ...formData, cancellationDeadlineHours: parseInt(e.target.value) })
                                }
                                disabled={creating}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 mb-4">
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.sameDayBooking}
                                onChange={(e) => setFormData({ ...formData, sameDayBooking: e.target.checked })}
                            />
                            <span className="text-sm">{t('reservations.sameDayBooking')}</span>
                        </label>
                        <label className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.requiresApproval}
                                onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                            />
                            <span className="text-sm">{t('reservations.requiresApproval')}</span>
                        </label>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={handleCreate} disabled={creating || !formData.name.trim()}>
                            {creating ? t('reservations.creating') : t('reservations.create')}
                        </Button>
                        <Button variant="outline" onClick={() => setShowNew(false)}>
                            {t('reservations.cancel')}
                        </Button>
                    </div>
                </div>
            )}

            {entityTypes.length === 0 ? (
                <EmptyState
                    title={t('reservations.noEntityTypes')}
                    text={t('reservations.noEntityTypesText')}
                />
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('reservations.name')}</TableHead>
                                <TableHead>{t('reservations.duration')}</TableHead>
                                <TableHead>{t('reservations.advanceBooking')}</TableHead>
                                <TableHead>{t('reservations.options')}</TableHead>
                                <TableHead>{t('reservations.actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entityTypes.map((entityType) => (
                                <TableRow key={entityType.id}>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: entityType.color }}
                                            />
                                            <span className="font-medium">{entityType.name}</span>
                                            <span className="text-gray-400 text-sm">({entityType.slug})</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {entityType.minDurationMinutes}-{entityType.maxDurationMinutes} min
                                    </TableCell>
                                    <TableCell>{entityType.advanceBookingDays} days</TableCell>
                                    <TableCell>
                                        <div className="flex gap-1">
                                            {entityType.sameDayBooking && <Badge variant="secondary">{t('reservations.sameDay')}</Badge>}
                                            {entityType.requiresApproval && <Badge variant="outline">{t('reservations.approval')}</Badge>}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Button variant="outline" onClick={() => handleEdit(entityType)}>
                                            {t('reservations.edit')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {isEditing && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">{t('reservations.editEntityType')}</h3>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.name')}</label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    disabled={updating}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.slug')}</label>
                                <Input
                                    value={formData.slug}
                                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                                    disabled={updating}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.minDuration')}</label>
                                <Input
                                    type="number"
                                    value={formData.minDurationMinutes}
                                    onChange={(e) =>
                                        setFormData({ ...formData, minDurationMinutes: parseInt(e.target.value) })
                                    }
                                    disabled={updating}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.maxDuration')}</label>
                                <Input
                                    type="number"
                                    value={formData.maxDurationMinutes}
                                    onChange={(e) =>
                                        setFormData({ ...formData, maxDurationMinutes: parseInt(e.target.value) })
                                    }
                                    disabled={updating}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.color')}</label>
                                <Input
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    disabled={updating}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reservations.cancellationDeadline')}</label>
                                <Input
                                    type="number"
                                    value={formData.cancellationDeadlineHours}
                                    onChange={(e) =>
                                        setFormData({ ...formData, cancellationDeadlineHours: parseInt(e.target.value) })
                                    }
                                    disabled={updating}
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-4 mb-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.sameDayBooking}
                                    onChange={(e) => setFormData({ ...formData, sameDayBooking: e.target.checked })}
                                />
                                <span className="text-sm">{t('reservations.sameDayBooking')}</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={formData.requiresApproval}
                                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                                />
                                <span className="text-sm">{t('reservations.requiresApproval')}</span>
                            </label>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={() => handleUpdate(isEditing)} disabled={updating}>
                                {updating ? t('reservations.saving') : t('reservations.save')}
                            </Button>
                            <Button variant="outline" onClick={() => setIsEditing(null)}>
                                {t('reservations.cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};
